import { useState } from 'react';
import type { PropsWithChildren } from 'react';
import { PageTitle } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/PageTitle';
import { getAuthenticationProvider } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { AuthorizationServiceClient } from '@johnlewispartnership/wtr-website-authentication/dist/service';
import {
  ExperimentsContextType,
  FeatureFlags,
  FragmentExperimentsProvider,
  FragmentFeatureFlagsProvider,
} from '@johnlewispartnership/wtr-website-decisioner-client/express';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import config from 'config';
import tokenServiceClient from 'services/token';
import Breadcrumbs from 'renderer/components/Breadcrumbs';
import type { ContentPageData } from 'services/content/types';
import CookieConsentModal from 'renderer/components/CookieConsent/CookieConsentModal';
import { CommonModalProvider } from 'renderer/components/CommonModal/context';
import CommonModal from 'renderer/components/CommonModal';
import { CookieConsentProvider } from 'renderer/components/CookieConsent/context';
import { PreloadedDataProvider } from 'renderer/context/preloadedDataContext';
import { PreloadedData } from 'server/types';
import { TrackedExperimentsProvider } from 'renderer/context/TrackedExperiments';

import PageViewTrackerEvent from './PageViewTrackerEvent';

const authorizationServiceClient = new AuthorizationServiceClient(
  config.services.authorizationServer.url,
);

const AuthenticationProvider = getAuthenticationProvider({
  tokenServiceClient,
  tokenServiceBasePath: config.services.tokenClient.path,
  authorizationServiceClient,
});

type PageLayoutProps = PropsWithChildren<{
  pageData: ContentPageData;
  preloadedData?: PreloadedData;
}>;

const PageLayout = ({ children, pageData, preloadedData }: PageLayoutProps) => {
  const [queryClient] = useState(() => new QueryClient());

  const { authorableBreadcrumb, breadcrumbs, pageTitle, showPageTitle } = pageData;
  const { display, text } = pageTitle || {};

  const accessToken = preloadedData?.token?.accessToken;
  const isLoggedIn = !!accessToken;

  return (
    <CookieConsentProvider>
      <AuthenticationProvider
        initialState={{
          initialised: isLoggedIn,
          isLoggedIn: isLoggedIn,
          hasTokenSession: !!preloadedData?.hasSessionToken,
          token: preloadedData?.token,
        }}
      >
        <QueryClientProvider client={queryClient}>
          <PageViewTrackerEvent />
          <CommonModalProvider>
            <FragmentFeatureFlagsProvider
              featureFlags={preloadedData?.featureFlags as FeatureFlags}
            >
              <FragmentExperimentsProvider
                experimentsContext={preloadedData?.experimentsContext as ExperimentsContextType}
              >
                <TrackedExperimentsProvider>
                  <PreloadedDataProvider preloadedData={preloadedData}>
                    <div
                      data-testid="container"
                      className="container-fluid"
                      style={{ width: '100%', padding: 0 }}
                    >
                      <Breadcrumbs
                        authorableBreadcrumb={authorableBreadcrumb}
                        breadcrumbs={breadcrumbs}
                      />
                      {showPageTitle && display && <PageTitle title={text} theme="underline" />}
                    </div>
                    {children}
                  </PreloadedDataProvider>
                </TrackedExperimentsProvider>
              </FragmentExperimentsProvider>
            </FragmentFeatureFlagsProvider>
            <CookieConsentModal />
            <CommonModal />
          </CommonModalProvider>
        </QueryClientProvider>
      </AuthenticationProvider>
    </CookieConsentProvider>
  );
};

export default PageLayout;
